'use client';

import cx from 'classnames';
import $ from 'jquery';
import PropTypes from 'prop-types';
import {useCallback, useEffect, useRef} from 'react';
import 'timepicker/jquery.timepicker.min.js';
import 'timepicker/jquery.timepicker.css';

import {InputText} from './InputText';

const DEFAULT_APPEND_TO = 'body';

export const TimePicker = ({
  id,
  step = 15,
  onChange,
  value,
  startOptionsAt,
  minTime,
  maxTime,
  'data-qa-id': qaId,
  'data-qa-value': qaValue,
  disabled = false,
  error,
  startAdornment = null,
  placeholder,
  label,
  required = false,
  hideSelectArrow = false,
  appendTo = DEFAULT_APPEND_TO,
  ...props
}) => {
  const timepicker = useRef(null);
  useEffect(() => {
    timepicker.current = $(`#${id}`).timepicker({
      scrollDefault: startOptionsAt ?? 'now',
      minTime,
      maxTime,
      timeFormat: 'g:i A',
      className: cx('timepicker', {
        'hidden-arrow': hideSelectArrow,
      }),
      step,
      closeOnWindowScroll: false,
      listWidth: 1,
      appendTo,
    });
    timepicker.current.on('focus', (e) => {
      e.target.select();
    });

    return () => {
      timepicker.current.off('focus');
      timepicker.current.timepicker('remove');
    };
  }, [id, startOptionsAt, minTime, maxTime, hideSelectArrow, step, appendTo]);

  useEffect(() => {
    timepicker.current.on('selectTime', (e) => {
      onChange(e.target);
      e.target.blur();
    });
    return () => {
      timepicker.current.off('selectTime');
    };
  }, [onChange]);

  useEffect(() => {
    $(`#${id}`).val(value);
  }, [id, value]);

  const detectEnter = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        onChange(e.target);
        timepicker.current.blur();
        document.querySelector(`#${id}__wrapper .ui-timepicker-wrapper`).style.display = 'none';
      }
    },
    [onChange, id]
  );

  const handleBlur = useCallback(
    (e) => {
      onChange(e.target);
      timepicker.current.blur();
    },
    [onChange]
  );

  return (
    <InputText
      {...props}
      defaultValue={value ?? ''}
      id={id}
      type="text"
      onChange={(e) => onChange(e.target)}
      onBlur={handleBlur}
      onKeyDown={detectEnter}
      value={value ?? ''}
      data-qa-id={qaId}
      data-qa-value={qaValue}
      disabled={disabled}
      error={error}
      startAdornment={startAdornment}
      placeholder={placeholder}
      label={label}
      required={required}
    />
  );
};

TimePicker.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  startOptionsAt: PropTypes.string,
  'data-qa-id': PropTypes.string,
  'data-qa-value': PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  error: PropTypes.string,
  translucent: PropTypes.bool,
  startAdornment: PropTypes.node,
  placeholder: PropTypes.string,
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  required: PropTypes.bool,
  showOptionalLabel: PropTypes.bool,
  hideSelectArrow: PropTypes.bool,
};
